import { Color, Spacing } from '@persona-id/design-tokens';
import { useContext } from 'react';
import styled, { css, ThemeContext } from 'styled-components';

import { Label } from 'design-system/components/Text';

const WEAK_STRENGTH = 35;
const FAIR_STRENGTH = 70;
const GOOD_STRENGTH = 85;

const Container = styled('div')`
  margin-bottom: ${Spacing[2]};
  width: 100%;

  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const IndicatorBar = styled('div')`
  margin-left: ${Spacing[4]};

  flex-grow: 1;
  display: flex;
`;

export const IndicatorBarCell = styled('div')`
  width: 100%;
  height: 8px;
  margin-right: 5px;

  background-color: ${({ theme, color }) => color || theme.color.background.disabled};
  ${({ theme }) => css`
    border-radius: ${theme.element.borderRadius};
  `}
`;

// Different prop name because props.color already exists on the label.
const StrengthLabel = styled(Label)<{ textColor?: Color }>`
  ${({ textColor }) =>
    textColor != null &&
    css`
      color: ${textColor};
    `}
  min-width: 100px;
`;

interface PasswordStrengthIndicatorProps {
  strength: number | null;
}

export default ({ strength }: PasswordStrengthIndicatorProps) => {
  // strength from 0 - 100
  // 70 or above is strong enough
  //
  // < 35 --> 1 bar
  // < 70 --> 2 bars
  // < 85 --> 3 bars
  // <=100 --> 4 bars

  const theme = useContext(ThemeContext);

  if (strength == null) {
    return (
      <Container>
        <StrengthLabel
          size='sm'
          textColor={theme.color.text.tertiary}
          data-test='password-strength-indicator__strength-label'
        >
          Password strength
        </StrengthLabel>
        <IndicatorBar data-test='password-strength-indicator__indicator-bar'>
          <IndicatorBarCell data-test='password-strength-indicator__indicator-bar-cell-0' />
          <IndicatorBarCell data-test='password-strength-indicator__indicator-bar-cell-1' />
          <IndicatorBarCell data-test='password-strength-indicator__indicator-bar-cell-2' />
          <IndicatorBarCell data-test='password-strength-indicator__indicator-bar-cell-3' />
        </IndicatorBar>
      </Container>
    );
  } else if (strength < WEAK_STRENGTH) {
    return (
      <Container>
        <StrengthLabel
          size='sm'
          data-test='password-strength-indicator__strength-label'
          textColor={theme.color.variants.danger.default}
        >
          Very weak
        </StrengthLabel>
        <IndicatorBar data-test='password-strength-indicator__indicator-bar'>
          <IndicatorBarCell
            data-test='password-strength-indicator__indicator-bar-cell-0'
            color={theme.color.variants.danger.default}
          />
          <IndicatorBarCell data-test='password-strength-indicator__indicator-bar-cell-1' />
          <IndicatorBarCell data-test='password-strength-indicator__indicator-bar-cell-2' />
          <IndicatorBarCell data-test='password-strength-indicator__indicator-bar-cell-3' />
        </IndicatorBar>
      </Container>
    );
  } else if (strength < FAIR_STRENGTH) {
    return (
      <Container>
        <StrengthLabel
          size='sm'
          data-test='password-strength-indicator__strength-label'
          textColor={theme.color.variants.danger.default}
        >
          Weak
        </StrengthLabel>
        <IndicatorBar data-test='password-strength-indicator__indicator-bar'>
          <IndicatorBarCell
            data-test='password-strength-indicator__indicator-bar-cell-0'
            color={theme.color.variants.danger.default}
          />
          <IndicatorBarCell
            data-test='password-strength-indicator__indicator-bar-cell-1'
            color={theme.color.variants.danger.default}
          />
          <IndicatorBarCell data-test='password-strength-indicator__indicator-bar-cell-2' />
          <IndicatorBarCell data-test='password-strength-indicator__indicator-bar-cell-3' />
        </IndicatorBar>
      </Container>
    );
  } else if (strength < GOOD_STRENGTH) {
    return (
      <Container>
        <StrengthLabel
          size='sm'
          data-test='password-strength-indicator__strength-label'
          textColor={theme.color.variants.caution.default}
        >
          Fair
        </StrengthLabel>
        <IndicatorBar data-test='password-strength-indicator__indicator-bar'>
          <IndicatorBarCell
            data-test='password-strength-indicator__indicator-bar-cell-0'
            color={theme.color.variants.caution.default}
          />
          <IndicatorBarCell
            data-test='password-strength-indicator__indicator-bar-cell-1'
            color={theme.color.variants.caution.default}
          />
          <IndicatorBarCell
            data-test='password-strength-indicator__indicator-bar-cell-2'
            color={theme.color.variants.caution.default}
          />
          <IndicatorBarCell data-test='password-strength-indicator__indicator-bar-cell-3' />
        </IndicatorBar>
      </Container>
    );
  } else {
    return (
      <Container>
        <StrengthLabel
          size='sm'
          data-test='password-strength-indicator__strength-label'
          textColor={theme.color.variants.success.default}
        >
          Strong
        </StrengthLabel>
        <IndicatorBar data-test='password-strength-indicator__indicator-bar'>
          <IndicatorBarCell
            data-test='password-strength-indicator__indicator-bar-cell-0'
            color={theme.color.variants.success.default}
          />
          <IndicatorBarCell
            data-test='password-strength-indicator__indicator-bar-cell-1'
            color={theme.color.variants.success.default}
          />
          <IndicatorBarCell
            data-test='password-strength-indicator__indicator-bar-cell-2'
            color={theme.color.variants.success.default}
          />
          <IndicatorBarCell
            data-test='password-strength-indicator__indicator-bar-cell-3'
            color={theme.color.variants.success.default}
          />
        </IndicatorBar>
      </Container>
    );
  }
};
